export interface BreadcrumbPoint {
  recorded_at: string;
  latitude: string;
  longitude: string;
  accuracy_m: number | null;
  heading_deg: number | null;
  speed_mps: number | null;
  altitude_m: number | null;
}

const MIN_FIX_INTERVAL_MS = 3 * 1000;
const MAX_BUFFERED_POINTS = 500;

// iOS reports unavailable values as null, but per the Geolocation spec heading
// is NaN while stationary — normalize both to null so the API never sees NaN.
function finiteOrNull(value: number | null): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function toPoint(position: GeolocationPosition): BreadcrumbPoint {
  const { latitude, longitude, accuracy, heading, speed, altitude } = position.coords;

  return {
    recorded_at: new Date(position.timestamp).toISOString(),
    // The ingest API stores 8 decimal places (~1mm); trim before it rejects longer values.
    latitude: latitude.toFixed(8),
    longitude: longitude.toFixed(8),
    accuracy_m: accuracy ?? null,
    heading_deg: finiteOrNull(heading),
    speed_mps: finiteOrNull(speed),
    altitude_m: finiteOrNull(altitude),
  };
}

export function createBreadcrumbBuffer() {
  let points: BreadcrumbPoint[] = [];
  let lastAcceptedAt = 0;

  return {
    accept(position: GeolocationPosition) {
      if (position.timestamp - lastAcceptedAt < MIN_FIX_INTERVAL_MS) {
        return;
      }
      lastAcceptedAt = position.timestamp;
      points.push(toPoint(position));
      points = points.slice(-MAX_BUFFERED_POINTS);
    },
    drain(): BreadcrumbPoint[] {
      const drained = points;
      points = [];
      return drained;
    },
    restore(unsent: BreadcrumbPoint[]) {
      points = [...unsent, ...points].slice(-MAX_BUFFERED_POINTS);
    },
    get size() {
      return points.length;
    },
  };
}
